import { StandardEvents } from '@shopify/events';

/** Solo se abre con ratón: en móviles y tabletas táctiles el icono sigue abriendo el carrito lateral. */
const HOVER_QUERY = window.matchMedia('(hover: hover) and (pointer: fine)');
const OPEN_DELAY = 120;
const CLOSE_DELAY = 280;
/** Margen de tolerancia (px) alrededor de la zona segura icono + hueco + panel. */
const TOLERANCE = 6;

/**
 * Vista previa del carrito que aparece al pasar el ratón por el icono del carrito.
 *
 * El contenido lo pinta Liquid (snippet `mini-carrito`) y se refresca pidiendo la sección
 * `mini-carrito` a la Section Rendering API cada vez que cambia el carrito.
 *
 * Mientras el ratón está sobre el icono o el panel se sigue su posición con `pointermove` y se
 * comprueba geométricamente si sigue dentro de la zona segura (icono, hueco hasta el panel y panel).
 * Los eventos pointerenter/pointerleave del panel no son fiables porque vive en la capa superior.
 */
class MiniCarrito extends HTMLElement {
  /** @type {HTMLElement | null} */
  #trigger = null;
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {AbortController | null} */
  #request = null;
  #openTimer = 0;
  #closeTimer = 0;
  #frame = 0;
  #tracking = false;
  #stale = false;
  #pointer = { x: -1, y: -1 };

  connectedCallback() {
    this.#trigger =
      this.closest('header-actions')?.querySelector('[aria-controls="cart-drawer"], .action__cart') ?? null;
    if (!this.#trigger || typeof this.showPopover !== 'function') return;

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.#trigger.addEventListener('pointerenter', this.#onTriggerEnter, { signal });
    this.#trigger.addEventListener('click', () => this.hide(), { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('keydown', (event) => event.key === 'Escape' && this.hide(), { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    window.addEventListener('pageshow', (event) => event.persisted && (this.#stale = true), { signal });
    // En captura: la página puede desplazarse dentro de un contenedor y no en window.
    document.addEventListener('scroll', this.#onViewportChange, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#onViewportChange, { passive: true, signal });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#request?.abort();
    this.#track(false);
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    cancelAnimationFrame(this.#frame);
  }

  get isOpen() {
    return this.matches(':popover-open');
  }

  get #itemCount() {
    return Number(this.querySelector('[data-cart-count]')?.getAttribute('data-cart-count') ?? 0);
  }

  show() {
    clearTimeout(this.#openTimer);
    if (this.isOpen || this.#itemCount === 0 || document.getElementById('cart-drawer')?.hasAttribute('open')) return;

    this.#position();
    this.showPopover();
  }

  hide() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = 0;
    this.#track(false);
    if (this.isOpen) this.hidePopover();
  }

  async refresh() {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;

    try {
      const url = `${window.Theme?.routes?.cart_url ?? '/cart'}?section_id=mini-carrito`;
      const response = await fetch(url, { signal: request.signal, cache: 'no-store' });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.querySelector('.mini-carrito__inner');
      const current = this.querySelector('.mini-carrito__inner');
      if (!fresh || !current) return;

      current.replaceWith(fresh);
      this.#stale = false;

      if (this.#itemCount === 0) this.hide();
      else if (this.isOpen) this.#position();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[mini-carrito] No se pudo actualizar:', error);
    }
  }

  /** @param {PointerEvent} event */
  #onTriggerEnter = (event) => {
    if (event.pointerType !== 'mouse' || !HOVER_QUERY.matches) return;

    this.#pointer = { x: event.clientX, y: event.clientY };
    this.#track(true);
    clearTimeout(this.#closeTimer);
    this.#closeTimer = 0;
    if (this.isOpen) return;

    clearTimeout(this.#openTimer);
    this.#openTimer = setTimeout(() => this.show(), OPEN_DELAY);
    if (this.#stale) {
      this.refresh().then(() => this.#tracking && this.#isInside(this.#pointer.x, this.#pointer.y) && this.show());
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;

    this.#pointer = { x: event.clientX, y: event.clientY };
    if (this.#isInside(event.clientX, event.clientY)) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = 0;
    } else {
      this.#leave();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerOut = (event) => {
    // Sin relatedTarget el ratón ha salido de la ventana.
    if (!event.relatedTarget) this.#leave();
  };

  #leave() {
    clearTimeout(this.#openTimer);
    if (!this.isOpen) {
      this.#track(false);
      return;
    }
    if (!this.#closeTimer) this.#closeTimer = setTimeout(() => this.hide(), CLOSE_DELAY);
  }

  /** @param {boolean} on */
  #track(on) {
    if (on === this.#tracking) return;

    this.#tracking = on;
    if (on) {
      document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
      document.addEventListener('pointerout', this.#onPointerOut);
    } else {
      document.removeEventListener('pointermove', this.#onPointerMove);
      document.removeEventListener('pointerout', this.#onPointerOut);
    }
  }

  /**
   * ¿Está el punto dentro del icono, o del panel abierto junto con el hueco que lo separa del icono?
   * @param {number} x
   * @param {number} y
   */
  #isInside(x, y) {
    if (!this.#trigger) return false;

    /** @param {{ left: number, right: number, top: number, bottom: number }} rect */
    const within = (rect) =>
      x >= rect.left - TOLERANCE && x <= rect.right + TOLERANCE && y >= rect.top - TOLERANCE && y <= rect.bottom + TOLERANCE;

    const trigger = this.#trigger.getBoundingClientRect();
    if (within(trigger)) return true;
    if (!this.isOpen) return false;

    const panel = this.getBoundingClientRect();
    return within({
      left: panel.left,
      right: Math.max(panel.right, trigger.right),
      top: trigger.top,
      bottom: panel.bottom,
    });
  }

  /**
   * "Ver carrito" abre el carrito lateral del tema en lugar de ir a la página /cart.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('[data-mini-carrito-view]') : null;
    const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.getElementById('cart-drawer'));
    if (!link || typeof drawer?.open !== 'function') return;

    event.preventDefault();
    this.hide();
    drawer.open();
  };

  /** @param {Event & { promise?: Promise<unknown> }} event */
  #onCartUpdate = (event) => {
    this.#stale = true;
    this.hide();
    event.promise?.then(() => this.refresh()).catch(() => {});
  };

  #onViewportChange = () => {
    if (!this.isOpen) return;

    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const rect = this.#trigger?.getBoundingClientRect();
      if (!rect || rect.bottom <= 0 || !HOVER_QUERY.matches) return this.hide();
      this.#position();
    });
  };

  /** Coloca el panel justo debajo de la fila de la cabecera, alineado con el icono del carrito. */
  #position() {
    if (!this.#trigger) return;

    const trigger = this.#trigger.getBoundingClientRect();
    const row = this.#trigger.closest('.header__row')?.getBoundingClientRect();
    const top = Math.round(row && row.bottom > trigger.bottom ? row.bottom : trigger.bottom + 8);
    const right = Math.max(16, Math.round(document.documentElement.clientWidth - trigger.right));

    this.style.setProperty('--mini-carrito-top', `${top}px`);
    this.style.setProperty('--mini-carrito-right', `${right}px`);
  }
}

if (!customElements.get('mini-carrito')) {
  customElements.define('mini-carrito', MiniCarrito);
}
